import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface TruncatedTextProps {
  /** The text content to display */
  text: string;
  /** Number of lines to clamp (1-4). Defaults to 1 */
  lines?: 1 | 2 | 3 | 4;
  /** Additional CSS classes to apply */
  className?: string;
  /** Whether to render as HTML content */
  dangerouslySetInnerHTML?: boolean;
}

/**
 * TruncatedText - Displays truncated text with a hover popover.
 * Popover is portaled to document.body so it escapes AG Grid / overflow:hidden containers.
 * The popover is scrollable for long content and stays open when hovered.
 */
const TruncatedText: React.FC<TruncatedTextProps> = ({
  text,
  lines = 1,
  className = '',
  dangerouslySetInnerHTML = false,
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0, placement: 'below' as 'above' | 'below' });
  const hideTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const isOverflowing = textRef.current.scrollHeight > textRef.current.clientHeight;
        setIsTruncated(isOverflowing);
      }
    };
    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [text]);

  const lineClampClass = {
    1: 'line-clamp-1',
    2: 'line-clamp-2',
    3: 'line-clamp-3',
    4: 'line-clamp-4',
  }[lines];

  const cancelHide = useCallback(() => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
      hideTimeout.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    cancelHide();
    hideTimeout.current = setTimeout(() => setShowPopover(false), 150);
  }, [cancelHide]);

  const handleTriggerEnter = () => {
    if (!isTruncated || !textRef.current) return;
    cancelHide();

    const rect = textRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const placement = spaceBelow >= 220 || spaceBelow > spaceAbove ? 'below' : 'above';

    const left = Math.max(8, Math.min(rect.left, window.innerWidth - 360));

    setPopoverPos({
      top: placement === 'below' ? rect.bottom + 6 : rect.top - 6,
      left,
      placement,
    });
    setShowPopover(true);
  };

  const handlePopoverEnter = () => cancelHide();
  const handlePopoverLeave = () => scheduleHide();

  useEffect(() => {
    if (!showPopover) return;
    const handleScroll = (e: Event) => {
      if (popoverRef.current?.contains(e.target as Node)) return;
      setShowPopover(false);
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [showPopover]);

  useEffect(() => {
    return () => cancelHide();
  }, [cancelHide]);

  const stripHtml = (html: string) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const popoverText = dangerouslySetInnerHTML ? stripHtml(text) : text;

  if (!text) return null;

  const popoverEl = showPopover && isTruncated ? (
    <div
      ref={popoverRef}
      onMouseEnter={handlePopoverEnter}
      onMouseLeave={handlePopoverLeave}
      className="fixed z-[9999] w-80 rounded-lg shadow-xl border border-border-light dark:border-border-dark bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary text-sm"
      style={{
        top: popoverPos.placement === 'below' ? `${popoverPos.top}px` : undefined,
        bottom: popoverPos.placement === 'above' ? `${window.innerHeight - popoverPos.top}px` : undefined,
        left: `${popoverPos.left}px`,
      }}
    >
      <div
        className="p-3 overflow-y-auto whitespace-pre-wrap break-words leading-relaxed"
        style={{ maxHeight: '280px' }}
      >
        {popoverText}
      </div>
    </div>
  ) : null;

  return (
    <>
      <div
        className="inline-block w-full"
        onMouseEnter={handleTriggerEnter}
        onMouseLeave={scheduleHide}
      >
        {dangerouslySetInnerHTML ? (
          <div
            ref={textRef}
            className={`${lineClampClass} ${className} ${isTruncated ? 'cursor-pointer' : ''}`}
            dangerouslySetInnerHTML={{ __html: text }}
          />
        ) : (
          <div
            ref={textRef}
            className={`${lineClampClass} ${className} ${isTruncated ? 'cursor-pointer' : ''}`}
          >
            {text}
          </div>
        )}
      </div>
      {popoverEl && createPortal(popoverEl, document.body)}
    </>
  );
};

export default TruncatedText;
